<script lang="ts" setup>
import {
  isLockMsg,
  replacePlaceHolder,
} from "@/components/mobile/chat/mode/util";
import { BubbleActionPopupInstance } from "@/components/mobile/chat/popup/BubbleActionPopup.vue";
import { useChatMessageList } from "@/composable/chat/useChatMessageList";
import { DEFAULT_COST, ModelList } from "@/enums/chatEnums";
import { useChatSession } from "@/stores/chatSession";
import { useMate } from "@/stores/mate";
import {
  ChatMessageItemInstance,
  IMessageItem,
  MessageListInstance,
} from "@/types/component";
import { marked } from "marked";
import { List } from "vant";
import { computed, nextTick, ref } from "vue";

const props = defineProps<{
  sessionId: number;
  mate?: Mate;
  myMessage: string;
  streamMsg: string;
  isPostingMsg: "start" | "end" | false;
  isStreamReplying: boolean;
  streamingNames?: string[];
  enableAvatar: boolean;
}>();

const mateStore = useMate();
const sessionStore = useChatSession();
const currentSession = computed(() => sessionStore.sessionMap[props.sessionId]);
// Element Ref
const messageContainerRef = ref<HTMLElement | null>(null);
const scrollRef = ref<HTMLDivElement | null>(null);
const bubbleActionRef = ref<BubbleActionPopupInstance | null>(null);
const chatMessageRefs = ref<ChatMessageItemInstance[]>([]);

const {
  messageList,
  scrollLoading,
  finished,
  error,
  scrollLoad,
  scrollToBottom,
} = useChatMessageList({
  sessionId: props.sessionId,
  messageContainerRef,
  scrollRef,
  myMessage: computed(() => props.myMessage),
  streamMsg: computed(() => props.streamMsg),
  isStreamReplying: computed(() => props.isStreamReplying),
});

const scenario = computed(() => {
  const scenarioText =
    currentSession.value?.chatScene?.scenario || props.mate?.scenario;
  if (!scenarioText || !props.mate?.name || !mateStore.currentMate?.name) {
    return "";
  }
  return marked.parse(
    replacePlaceHolder(
      scenarioText,
      props.mate.name,
      mateStore.currentMate?.name
    )
  );
});
const isPrivate = computed(() => {
  if (!currentSession.value) {
    return false;
  }
  return currentSession.value?.isPrivate === 1;
});
const unlockCost = computed(() =>
  Number(
    ModelList.find(
      (item) =>
        item.value === currentSession.value?.model &&
        item.costType === "crystal"
    )?.cost ?? DEFAULT_COST
  )
);
const playAudioMsg = (id: number) => {
  nextTick(() => {
    const target = chatMessageRefs.value.find((item) => item.id === id);
    if (target) {
      target.audioRef?.playAudio();
    }
  });
};

defineExpose<MessageListInstance>({
  scrollToBottom: scrollToBottom,
  playAudioMsg: playAudioMsg,
});

const avatarList = (item: IMessageItem) => {
  const charList = currentSession.value.chatScene?.characterzz;
  const fromNames = item.fromNames.split(",").filter(Boolean);
  if (
    item.isCurrentUser ||
    !charList ||
    charList.length <= 1 ||
    fromNames.length === 0
  ) {
    return [item.mediaUrl];
  }
  const avatarList = fromNames.map(
    (name) =>
      charList.find((item) => item?.mate?.name === name)?.mate?.mediaUrl ?? ""
  );
  if (avatarList.length === 0) {
    return [item.mediaUrl];
  }

  return avatarList;
};

const streamingAvatarList = computed(() => {
  const charList = currentSession.value.chatScene?.characterzz;
  if (!charList || charList.length <= 1 || props.streamingNames?.length === 0) {
    return props.mate?.mediaUrl ? [props.mate?.mediaUrl] : [];
  }
  const avatarList =
    props.streamingNames?.map(
      (name) =>
        charList.find((item) => item?.mate?.name === name)?.mate?.mediaUrl ?? ""
    ) ?? [];
  if (avatarList.length === 0) {
    return props.mate?.mediaUrl ? [props.mate?.mediaUrl] : [];
  }
  return avatarList;
});
</script>

<template>
  <div ref="scrollRef" class="h-full overflow-y-scroll">
    <div v-if="!mate || !currentSession"></div>
    <list
      v-else
      v-model:error="error"
      v-model:loading="scrollLoading"
      :error-text="$t('Message.requestFailedClickToReload')"
      :finished="finished"
      :finished-text="$t('Message.noMoreAvailable')"
      :immediate-check="false"
      direction="up"
      offset="0"
      @load="scrollLoad"
    >
      <div
        v-if="scenario && isPrivate"
        class="flex items-center justify-center p-2 border-b 0"
      >
        <div class="text-base-content/50">
          <span class="font-semibold"
            >{{ $t("User.mate.module.chatScene") }}: </span
          ><span class="chat-container" v-html="scenario"></span>
        </div>
      </div>
      <div ref="messageContainerRef" class="p-2">
        <RetroMessageItem
          v-for="item in messageList"
          :item="item"
          :id="item.id"
          :key="item.id"
          ref="chatMessageRefs"
          :avatar-url="item.mediaUrl"
          :enable-avatar-url="false"
          :is-locked="isLockMsg(item)"
          :mate-uuid="item.mateUuid"
          :msg="item.msg"
          :require-unlock="item.requireUnlock"
          :unlock-cost="unlockCost"
          :avatar-list="avatarList(item)"
          enable-avatar-click
        />
        <div>
          <RetroMessageItem
            v-show="myMessage"
            :avatar-url="mateStore.currentMate!.mediaUrl"
            :enable-avatar-url="false"
            :mate-uuid="mateStore.currentMate!.uuid"
            :msg="myMessage"
            enable-avatar-click
          />
          <!-- StreamMsg返回中 -->
          <RetroMessageItem
            v-show="streamMsg"
            :avatar-url="isPostingMsg === 'start' ? mate!.mediaUrl : mateStore.currentMate?.mediaUrl"
            :avatar-list="streamingAvatarList"
            :enable-avatar-url="false"
            :mate-uuid="mate!.uuid"
            :enable-avatar="enableAvatar"
            :msg="streamMsg"
            enable-avatar-click
            enable-parse-md
          />
          <!-- StreamMsg返回前的loading -->
          <RetroMessageItem
            v-show="!streamMsg && isPostingMsg"
            :enable-avatar="enableAvatar"
            :avatar-url="isPostingMsg === 'start' ? mate!.mediaUrl : mateStore.currentMate?.mediaUrl"
            :avatar-list="streamingAvatarList"
            :enable-avatar-url="false"
            :mate-uuid="mate!.uuid"
            enable-avatar-click
          >
            <div class="flex items-center">
              <span class="loading loading-dots loading-sm text-base-content" />
            </div>
          </RetroMessageItem>
        </div>
      </div>
    </list>
    <BubbleActionPopup ref="bubbleActionRef" />
  </div>
</template>
